import React, { useState, useEffect } from "react";
import { fromLonLat } from "ol/proj";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";

const resolveThumbnailUrl = (thumbnail) => {
  if (!thumbnail || typeof thumbnail !== "string") return null;
  const trimmed = thumbnail.trim();
  if (
    /^(?:javascript|data|vbscript):/i.test(trimmed) ||
    trimmed.startsWith("//")
  ) {
    return null;
  }
  if (trimmed.startsWith("https://") || trimmed.startsWith("http://")) {
    return trimmed;
  }
  return `https://ems.dimu.org/image/${encodeURIComponent(trimmed)}`;
};

export const LivingPinsOverlay = ({
  map,
  waypoints = [],
  activeWaypointIndex = 0,
  prefersReducedMotion = false,
  onSelectWaypoint = () => {},
}) => {
  const t = useScrollytellTranslation();
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!map || typeof map.on !== "function") return;
    const handleUpdate = () => setTick((t) => t + 1);

    map.on("postrender", handleUpdate);
    map.on("moveend", handleUpdate);

    return () => {
      if (typeof map.un === "function") {
        map.un("postrender", handleUpdate);
        map.un("moveend", handleUpdate);
      }
    };
  }, [map]);

  if (!Array.isArray(waypoints) || waypoints.length === 0) {
    return null;
  }

  const visibleWaypoints = waypoints.filter(
    (wp) => !String(wp?.id || "").startsWith("legacy-")
  );

  if (visibleWaypoints.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 4,
        overflow: "hidden",
      }}
    >
      {visibleWaypoints.map((wp, index) => {
        const isActive = index === activeWaypointIndex;
        let pixel = [0, 0];

        if (map && typeof map.getPixelFromCoordinate === "function") {
          try {
            pixel = map.getPixelFromCoordinate(fromLonLat(wp.coordinate)) || [0, 0];
          } catch {
            pixel = [0, 0];
          }
        }

        const [x, y] = pixel;
        const thumbnailUrl = resolveThumbnailUrl(wp.thumbnail);
        const ariaLabel = t(
          "livingPins.pinAriaLabel",
          `Stopp ${index + 1}: ${wp.title || "Stoppested"}`
        );

        return (
          <div
            key={wp.id || index}
            style={{
              position: "absolute",
              left: `${x}px`,
              top: `${y}px`,
              transform: "translate(-50%, -50%)",
              pointerEvents: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Accessible interactive Pin Button (>=44x44px target) */}
            <button
              type="button"
              aria-label={ariaLabel}
              aria-current={isActive ? "step" : undefined}
              onClick={() => onSelectWaypoint(index)}
              style={{
                position: "relative",
                width: "44px",
                height: "44px",
                padding: 0,
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: isActive ? 2 : 1,
              }}
            >
              {/* Sonar pulse ring for active pin */}
              {isActive && !prefersReducedMotion && (
                <div
                  className="sonarPulse"
                  data-testid="sonar-pulse"
                  style={{
                    position: "absolute",
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(56, 189, 248, 0.4)",
                    border: "2px solid #38BDF8",
                    animation: "sonarAnimation 2s infinite ease-out",
                    pointerEvents: "none",
                  }}
                />
              )}

              {/* Compact visible pin circle */}
              <div
                style={{
                  position: "relative",
                  width: isActive ? (thumbnailUrl ? "30px" : "26px") : (thumbnailUrl ? "26px" : "22px"),
                  height: isActive ? (thumbnailUrl ? "30px" : "26px") : (thumbnailUrl ? "26px" : "22px"),
                  backgroundColor: isActive ? "#0284C7" : "#0F172A",
                  color: "#FFFFFF",
                  border: isActive ? "2px solid #FFFFFF" : "1.5px solid #CBD5E1",
                  borderRadius: "50%",
                  boxShadow: isActive
                    ? "0 0 12px rgba(56, 189, 248, 0.7), 0 2px 8px rgba(0, 0, 0, 0.4)"
                    : "0 2px 6px rgba(0, 0, 0, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  transition: prefersReducedMotion ? "none" : "all 0.25s ease",
                }}
              >
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt={wp.title || ariaLabel}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {index + 1}
                  </span>
                )}
              </div>
            </button>

            {/* Title label pill on all waypoints with title */}
            {wp.title && (
              <div
                style={{
                  marginTop: "-4px",
                  backgroundColor: isActive
                    ? "rgba(15, 23, 42, 0.92)"
                    : "rgba(15, 23, 42, 0.78)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  color: isActive ? "#FFFFFF" : "#F1F5F9",
                  padding: isActive ? "2px 8px" : "2px 7px",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: isActive ? 600 : 500,
                  whiteSpace: "nowrap",
                  boxShadow: isActive
                    ? "0 2px 8px rgba(0, 0, 0, 0.35)"
                    : "0 2px 6px rgba(0, 0, 0, 0.25)",
                  border: isActive
                    ? "1px solid rgba(56, 189, 248, 0.55)"
                    : "1px solid rgba(255, 255, 255, 0.2)",
                  pointerEvents: "none",
                }}
              >
                {wp.title}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
